import useResponsive from "hooks/useResponsive";
import useNetworkStatus from "hooks/useNetworkStatus";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/routes";
import useDomain from "hooks/useDomain";
import useLocalStorage from "hooks/useLocalStorage";
import useModals from "hooks/useModals";
import { useConfigPromp } from "routes/context";
import { Statuses } from "utils/InterfaceAndTypeGeneral";
import useGetCodeError from "hooks/useGetCodeError";
import { ERROR_ADMISSION_NOT_FOUND, ERROR_ADMISSION_STARTED } from "utils/ConstError";
import useInteractionTracker from "hooks/useInteractionTracker";
import { FROM_START_EXAMEN, FROM_SUMMARY_EXAM } from "utils/ConstantsAndNum";

const useSummary = () => {
  const navigate = useNavigate();
  const isOnline = useNetworkStatus();
  const { isMinorTablet } = useResponsive();
  const {markInteractionStart,markInteractionEnd} = useInteractionTracker();
  const { setBlocking, setHeader, isTabIndex } = useConfigPromp();
  const getCodeError = useGetCodeError();
  const {
    isOpenModal,
    openModal,
    closeModal,
    registerModal,
    unregisterModal,
    getConfigModal,
  } = useModals();
  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true);
  const [isNotAssigned, setIsNotAssigned] = useState<boolean>(false);
  const [isStartedExam, setIsStartedExam] = useState<boolean>(false);
  const [errorServer, setValue, removeStorage] = useLocalStorage(
    "error_summary",
    0
  );
  const {
    run: runSummary,
    data: dataSummary,
    isError: isErrorSummary,
    isLoading: isLoadingSummary,
  } = useDomain();

  const {
    run: runStart,
    isLoading: isLoadingStart,
    isError: isErrorStart,
  } = useDomain();

  const handleOpenModalConfirmation = () => {
    if (!isOnline) return openModal("internet");
    openModal("confirmation");
  };

  const handleGetSummary = async () => {
    markInteractionStart(FROM_SUMMARY_EXAM, "Vista resumen examen");
    const { error, data } = await runSummary((domain: any) =>
        domain.get_summary_use_case.execute()
    );
    markInteractionEnd(FROM_SUMMARY_EXAM, "Vista resumen examen");
    if (data?.isDelivered || data?.isOutOfDate ) {
      return navigate(ROUTES.candidate.delivery);
    } else {
      setIsLoadingFirst(false);
      if (error) {
        if(getCodeError(error) === ERROR_ADMISSION_NOT_FOUND) {
          return setIsNotAssigned(true);
        }
        return setValue(errorServer + 1);
      }
    }
  };

  const handlePostStart = () => {
    return runStart((domain: any) =>
      domain.post_start_use_case.execute(
        dataSummary.admissionExamId,
        dataSummary.quizId
      )
    );
  };

  const handleRedirectToEvaluation = () => {
    if (dataSummary?.isInProgress) {
      markInteractionStart(FROM_START_EXAMEN, "Iniciar examen");
      return navigate(
        ROUTES.candidate.admission(
          dataSummary.admissionExamId,
          dataSummary.quizId
        )
      );
    } else {
      handleOpenModalConfirmation();
    }
  };

  const handleRedirect = () => {
    removeStorage();
    navigate(
      ROUTES.candidate.admission(
        dataSummary.admissionExamId,
        dataSummary.quizId
      )
    );
  }
  const handleNavigateAdmission = async () => {
    closeModal("confirmation");
    closeModal("internet");
    markInteractionStart(FROM_START_EXAMEN, "Iniciar examen");
    const { error } = await handlePostStart();
    if (error) {
      markInteractionEnd(FROM_START_EXAMEN, "Iniciar examen");
      if(getCodeError(error) === ERROR_ADMISSION_STARTED) {
        setIsStartedExam(true)
        return handleRedirect();
      }
      return setValue(errorServer + 1);
    }
    handleRedirect();
  };

  useEffect(() => {
    registerModal("confirmation", {
      onConfirm: handleNavigateAdmission,
      onCancel: () => closeModal("confirmation"),
    });

    registerModal("internet");

    return () => {
      unregisterModal("summary");
      unregisterModal("internet");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerModal, unregisterModal,dataSummary]);

  useEffect(() => {
    handleGetSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHeader(true);
    setBlocking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOpenModal,
    isMinorTablet,
    isNotAssigned,
    getConfigModal,
    handleNavigateAdmission,
    handleRedirectToEvaluation,
    isManyError: errorServer > 1,
    titleSummary: dataSummary?.title,
    statusSummary: dataSummary?.status,
    tabIndexValue: isTabIndex ? 0 : -1,
    isScheduled: dataSummary?.isScheduled,
    isInProgress: dataSummary?.isInProgress,
    isError: (isErrorStart || isErrorSummary) && !isStartedExam,
    statusNameSummary: dataSummary?.statusName,
    durationSummary: dataSummary?.durationName,
    timeSummary: dataSummary?.availableFrom?.time,
    instructionsSummary: dataSummary?.instructions,
    dateSummary: dataSummary?.availableFrom?.alertB,
    totalQuestionSummary: dataSummary?.numberOfQuestions,
    isLoading: isLoadingFirst || isLoadingSummary || isLoadingStart,
    isNotDelivered: dataSummary?.isNotDelivered,
    status:  dataSummary?.isNotDelivered ? dataSummary?.status : Statuses.OTHER,
  };
};

export default useSummary;
